import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SelectOption } from 'src/app/interfaces/search.interface';
import { Universe } from '../interfaces/universe.interface';
import { Country, Position } from '../interfaces/player.interface';
import { PositionsService } from './positions.service';
import { LoadingController } from '@ionic/angular';

@Injectable({
	providedIn: 'root'
})
export class SharedService {
	private http = inject(HttpClient);
	private translateService = inject(TranslateService);
	private positionsService = inject(PositionsService);
	private loadingCtrl = inject(LoadingController);

	public httpHost = environment.httpHost;

	public iconsFontSet:string = 'material-symbols-outlined';

	public urls = {
		cdn: 'https://cdn.scouz.com/',
		auth: {
			login: `${this.httpHost}/api/auth/login`,
			logout: `${this.httpHost}/api/auth/logout`,
			refresh: `${this.httpHost}/api/auth/refresh`,
			recover: `${this.httpHost}/api/auth/recover`,
			reset: `${this.httpHost}/api/auth/reset`,
		},
		dashboard: {
			get: `${this.httpHost}/api/dashboard/get`,
			download: {
				weekly: `${this.httpHost}/api/dashboard/download/weekly`,
				users: `${this.httpHost}/api/dashboard/download/users`,
			}
		},
		players: {
			get: `${this.httpHost}/api/players/get`,
			filters: `${this.httpHost}/api/players/filters`,
			positions: `${this.httpHost}/api/players/positions`,
			ratings: `${this.httpHost}/api/players/ratings`,
			list: `${this.httpHost}/api/players/list`,
			update: {
				rating: `${this.httpHost}/api/players/update/rating`,
				assessment: `${this.httpHost}/api/players/update/assessment`,
				favorite: `${this.httpHost}/api/players/update/favorite`
			},
			country: `${this.httpHost}/api/players/country`,
			state: `${this.httpHost}/api/players/state`,
			category: `${this.httpHost}/api/players/category`,
			reports: {
				get: `${this.httpHost}/api/players/reports/get`,
				save: `${this.httpHost}/api/players/reports/save`,
				sync: `${this.httpHost}/api/players/reports/sync`
			},
			events: `${this.httpHost}/api/events/player/{id}`
		},
		convocation: {
			get: `${this.httpHost}/api/convocation/get`,
			add: `${this.httpHost}/api/convocation/add`,
			send: `${this.httpHost}/api/convocation/send`
		},
		events: {
			save: `${this.httpHost}/api/events/save`,
			finish: `${this.httpHost}/api/events/finish`,
		    get: `${this.httpHost}/api/events/get`,
			notes_audio: `${this.httpHost}/api/events/{id}/notes/audio/{id_note}`,
			// players_search: `${this.httpHost}/api/players/search/events`,
			ratings: `${this.httpHost}/api/events/data/ratings`,
			event: {
				buttons: `${this.httpHost}/api/events/data/buttons`,
				report: `${this.httpHost}/api/events/{id}/report`,
			}
		},
		users: {
			get: `${this.httpHost}/api/users/get`,
			current: `${this.httpHost}/api/users/current`,
			current_edit: `${this.httpHost}/api/users/current/edit`,
			universes: `${this.httpHost}/api/users/universes`,
			universe: `${this.httpHost}/api/users/universe/set`,
			add: `${this.httpHost}/api/users/add`,
			edit: `${this.httpHost}/api/users/edit`
		},
		notifications: {
			count: `${this.httpHost}/api/notifications/count`,
			send: `${this.httpHost}/api/notifications/send`
		},
		database: {
			get: `${this.httpHost}/api/database/get`,
			dl: `${this.httpHost}/api/database/download`,
			job: `${this.httpHost}/api/database/job`,
			status: `${this.httpHost}/api/database/status`,
			procedure: `${this.httpHost}/api/database/procedure`
		},
		videos: {
			get: `${this.httpHost}/api/videos`,
			filters: `${this.httpHost}/api/videos/filters`,
			ranking: `${this.httpHost}/api/videos/ranking`
		},
		chats: {
			last: `${this.httpHost}/api/chats/last`,
			get: `${this.httpHost}/api/chats/get`,
			post: `${this.httpHost}/api/chats/post`
		},
		cron: {
			get: `${this.httpHost}/api/cron/get`,
			run: `${this.httpHost}/api/cron/run`
		},
	};

	public universes: Universe[] = [];
	public filters_players:any = {};
	public filters_videos:any = {};
	public players_positions:any = [];
	public players_ratings:any = [];
	public players_assessments:any = [];
	public countries:Country[] = [];
	public states:{[country:number]:any[]} = {};
	public categories:any[] = [];

	

	async getFiltersPlayers(should_reload:boolean = false) {
		if (Object.keys(this.filters_players).length==0 || should_reload) {
			const url = this.urls.players.filters;
			try {
				const res: any = await firstValueFrom(this.http.get(url));
				this.filters_players = res;
			} catch (error) {
				console.error("getFiltersPlayers error", error);
			}
		}

		return this.filters_players;
	}

	async getFiltersVideos(should_reload:boolean = false) {
		if (Object.keys(this.filters_videos).length==0 || should_reload) {
			const url = this.urls.videos.filters;
			try {
				const res: any = await firstValueFrom(this.http.get(url));
				this.filters_videos = res;
			} catch (error) {
				console.error("getFiltersVideos error", error);
			}
		}

		return this.filters_videos;
	}

	async getPositions(should_reload:boolean = false) {
		if (Object.keys(this.players_positions).length==0 || should_reload) {
			const url = this.urls.players.positions;
			try {
				const res: any = await firstValueFrom(this.http.get(url));
				// añadir el nombre de las posiciones 
				const posArr:Position[] = [];
				for (const pos of res) {
					const pos_trad = this.positionsService.STATIC_POSITIONS[pos.cod];
					if (pos_trad) {
						pos.cod_es = pos_trad.cod_es;
						pos.cod_en = pos_trad.cod_en;
						pos.name_en = pos_trad.name_en;
					}
					posArr.push(pos);
				}
				this.players_positions = posArr;
			} catch (error) {
				console.error("getPositions error", error);
			}
		}

		return this.players_positions;
	}

	async getPositionRatings(should_reload:boolean = false) {
		if (Object.keys(this.players_ratings).length==0 || should_reload) {
			const url = this.urls.players.ratings;
			try {
				const res: any = await firstValueFrom(this.http.get(url));
				this.players_ratings = res;
			} catch (error) {
				console.error("getPositionRatings error", error);
			}
		}

		return this.players_ratings;
	}

	async getPlayersAssessments() {
		if (!this.players_assessments.length) {
			['a', 'b', 'c', 'd', 'e'].forEach(v => {
				this.players_assessments.push({
					id: v,
					name: this.translateService.instant(`players.assessment.${v}`)
				});
			});
		}
		return this.players_assessments;
	}

	isLoadingUniverses:boolean = false;
	async getUniverses(should_reload:boolean = false): Promise<Universe[]> {
		return new Promise( async (resolve) => {
			if (this.isLoadingUniverses) {
				setTimeout(async () => {
					resolve(await this.getUniverses(should_reload));
				}, 100);
			} else {
				this.isLoadingUniverses = true;
				if ((this.universes.length==0 || should_reload)) {
					const url = this.urls.users.universes;
					try {
						const res: Universe[] = await firstValueFrom(this.http.get(url)) as Universe[];
						this.universes = res;
					} catch (error) {
						console.error("getUniverses error", error);
					}
				}
				this.isLoadingUniverses = false;
				resolve(this.universes);
			}
		});
	}

	public optsBoolean:SelectOption[] = [
		{value: 1, text: this.translateService.instant("general.yes")},
		{value: 0, text: this.translateService.instant("general.no")}
	];

	public optsEventRatingOperator:SelectOption[] = [
		{ value: '=', text: this.translateService.instant("general.compare.eq") },
		{ value: '>=', text: this.translateService.instant("general.compare.gteq") },
		{ value: '>', text: this.translateService.instant("general.compare.gt") },
		{ value: '<', text: this.translateService.instant("general.compare.lt") },
		{ value: '<=', text: this.translateService.instant("general.compare.lteq") },
	];

	public optsEventRatingValue:SelectOption[] = Array.from({ length: 10 }, (_, i) => ({
		value: i + 1,
		text: `${(i + 1)}`
	}));
	
	async getCountries() {
		if (this.countries.length==0) {
			const url = this.urls.players.country;
			try {
				const res: Country[] = await firstValueFrom(this.http.get(url)) as Country[];
				this.countries = res;
			} catch (error) {
				console.error("getCountries error", error);
			}
		}
		return this.countries;
	}

	async getStates(country:number) {
		if (typeof this.states[country] == 'undefined') {
			const url = `${this.urls.players.state}?country=${country}`;
			try {
				const res: any[] = await firstValueFrom(this.http.get(url)) as any[];
				this.states[country] = res;
			} catch (error) {
				console.error("getStates error", error);
			}
		}
		return this.states[country];
	}

	async getCategories() {
		if (this.categories.length==0) {
			const url = this.urls.players.category;
			try {
				const res: any[] = await firstValueFrom(this.http.get(url)) as any[];
				this.categories = res;
			} catch (error) {
				console.error("getCategories error", error);
			}
		}
		return this.categories;
	}


	loading_prevent: boolean = false;
	loading?:HTMLIonLoadingElement;
	loading_is_visible: boolean = false;
	async fullScreenLoading(action: 'show'|'hide') {
		// console.log(`fullScreenLoading(${action})`)
		if (this.loading_prevent) return;
		if (action=='show' && !this.loading_is_visible) {
			this.loading = await this.loadingCtrl.create({
				spinner: 'crescent',
				cssClass: 'fullscreen',
				backdropDismiss: false,
				showBackdrop: true,
				message: this.translateService.instant("general.loading")
			});
			this.loading.onDidDismiss().then(() => {
				this.loading_is_visible = false;
			});
			this.loading_is_visible = true;
			await this.loading.present();
		} else if (action=='hide' && this.loading) {
			await this.loading.dismiss();
		}	
	}

	async startBatchLoading() {
		await this.fullScreenLoading('show');
		this.loading_prevent = true;
	}
	async endBatchLoading() {
		this.loading_prevent = false;
		await this.fullScreenLoading('hide');
	}
}